"use client";

import Link from "next/link";
import FAQList from "./FAQList";
import { GoArrowRight } from "react-icons/go";
import { useGetFAQQuery } from "@/store/api/faqApi";
import { useMemo } from "react";
import type { FAQModule } from "@/types/api";

const HelpCenter = () => {
  // Fetch FAQ data from API
  const { 
    data: faqResponse, 
    isLoading 
  } = useGetFAQQuery();

  // Extract modules from API response and sort by priority
  const modules: FAQModule[] = useMemo(() => {
    if (faqResponse?.data) {
      return [...faqResponse.data].sort((a, b) => a.modulePriority - b.modulePriority);
    }
    return [];
  }, [faqResponse]);

  // Get the first module name as default type for FAQList
  const defaultModuleType = useMemo(() => {
    if (modules.length > 0) {
      return modules[0].moduleName.toLowerCase();
    }
    return "";
  }, [modules]);

  return (
    <div className="lg:h-217.25 flex items-center bg-bg3 dark:bg-dark-bg1">
      <div className="container lg:py-0 py-20">
        <div className="flex justify-between lg:items-end items-start lg:flex-row flex-col">
          <div className="max-w-160 w-full shrink-0">
            <p className="section-title dark:text-text13! max-lg:max-w-[374px] max-lg:leading-[15px]">
              Help Center
            </p>
            <div className="w-full max-w-125 h-1.5 my-5">
              <div className="w-full h-full bg-linear-to-r from-[#e981363d] to-transparent" />
            </div>
            <h2 className="text-text7 max-lg:text-[#262626] dark:text-white text-3xl lg:text-[40px] leading-10! max-lg:max-w-[374px] max-lg:leading-[32px]">
              <strong className="text-primary font-black">DALAL</strong> Help
              Center
            </h2>
            <p className="text-text7 max-lg:text-[#262626] dark:text-white text-xs lg:text-[17.6px] mt-5">
              <strong>Expert support and proven solutions</strong> – your
              tiger's claws for the affiliate adventure! We clear jungles of
              confusion so you enjoy smooth promotions and uninterrupted
              earnings.
            </p>
          </div>
          <div className="border border-border2 dark:border-white/10 p-5 lg:p-2.5 rounded-2xl flex gap-1.5 flex-wrap">
            {isLoading ? (
              <div className="h-10 w-full flex items-center justify-center text-text7 max-lg:text-[#262626] dark:text-white">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary"></div>
              </div>
            ) : modules.length > 0 ? (
              modules.map((module, index) => {
                const moduleNameLower = module.moduleName.toLowerCase();
                const isFirst = index === 0;
                
                return (
                  <Link
                    key={module.moduleId}
                    className={`h-10 ${isFirst ? 'first:w-34.5 first:text-white first:bg-text5' : ''} text-black dark:text-white w-fit grid place-items-center rounded-xl ${isFirst ? 'first:px-0' : 'px-2.5'} text-xs lg:base font-semibold capitalize transition-all`}
                    href="/help-center"
                  >
                    {module.moduleName}
                  </Link>
                );
              })
            ) : (
              <div className="h-10 w-full flex items-center justify-center text-text7 max-lg:text-[#262626] dark:text-white text-xs">
                No modules available
              </div>
            )}
          </div>
        </div>

        <FAQList type={defaultModuleType} />

        <Link
          className="btn border-2 border-border3 dark:border-white lg:w-fit ms-auto mt-7.5 py-3! max-lg:text-[#5D5D5D] dark:text-white group relative overflow-hidden hover:text-white hover:bg-text5 hover:border-text5 transition-all"
          href="/help-center"
        >
          <span>
            Got to <strong>Help Center</strong>
          </span>
          <GoArrowRight className="text-3xl text-text5 group-hover:text-white transition-all" />
        </Link>
      </div>
    </div>
  );
};

export default HelpCenter;
